import { VerifiedBadge } from "../components/VerifiedBadge";
import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layout } from "../components/Navbar";
import { api, getUser } from "../api";
import { toast } from "sonner";
import { Avatar, PostComposer, PostList } from "../components/Posts";
import { FollowersButton } from "../components/FollowersButton";
import { ShareButton } from "../components/ShareButton";
import { ShareMenu } from "../components/ShareMenu";
import { ShopTab } from "../components/ShopTab";
import { useSocialMeta } from "../lib/socialMeta";
import { formatProName } from "../lib/proName";
import { orgTypeLabel } from "../lib/orgTypes";
import { PublicBio } from "../components/PublicBio";

export default function OrganizationDetail({ idOverride } = {}) {
  const params = useParams();
  const id = idOverride || params.id;
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const u = getUser();
  const [org, setOrg] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [err, setErr] = useState(null);
  const [postsKey, setPostsKey] = useState(0);
  const [hasEvents, setHasEvents] = useState(null); // null = non ancora determinato
  const [tab, setTab] = useState("posts");
  const [events, setEvents] = useState([]);
  const [services, setServices] = useState([]);
  const [openCartShop, setOpenCartShop] = useState(false);
  const [sp] = useSearchParams();
  const load = () => api.get(`/organizations/${id}`).then(({data}) => { setOrg(data.org); setIsFollowing(data.is_following); }).catch(() => setErr(t("auth.org_not_found")));
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    // Stato reale: la lista filtrata (senza conteggi) decide se mostrare il pulsante attivo o disabilitato
    api.get("/events", { params: { organization_id: id, include_past: 1 } }).then(({data}) => { setEvents(data); setHasEvents(data.some(e => !e.is_past)); }).catch(() => { setHasEvents(false); setEvents([]); });
  }, [id]);
  useEffect(() => { api.get(`/organizations/${id}/services`).then(({data}) => setServices(data)).catch(() => setServices([])); }, [id]);
  useEffect(() => { const qt = sp.get("tab"); if (qt) setTab(qt); }, [sp]);
  // Meta social dinamici (OG/Twitter) nell'<head> della SPA per la route pubblica reale.
  useSocialMeta(org ? {
    title: org.org_name,
    description: [org.city, org.country].filter(Boolean).join(" · ") || "Struttura sanitaria su YouPAM",
    image: org.avatar_url || org.cover_url,
    url: `${window.location.origin}/organizations/${id}`,
    type: "profile",
  } : null);
  const follow = async () => {
    try {
      if (isFollowing) await api.post(`/professionals/${id}/unfollow`);
      else await api.post(`/professionals/${id}/follow`);
      load();
    } catch(e) { toast.error(e.response?.data?.detail || t("events.error_generic")); }
  };
  const requireAuthAction = (action) => {
    localStorage.setItem("pending_action", JSON.stringify({ type: action, kind: "org", id }));
    nav(`/login?next=${encodeURIComponent(`/organizations/${id}`)}`);
  };
  const onFollow = () => { if (!u) return requireAuthAction("follow"); follow(); };
  const onChat = () => { if (!u) return requireAuthAction("chat"); nav(`/chat/${id}`); };
  // Azione pendente da profilo pubblico anonimo: eseguita UNA sola volta dopo l'auth e SOLO per pazienti.
  useEffect(() => {
    if (!u || !org) return;
    const raw = localStorage.getItem("pending_action");
    if (!raw) return;
    let pa; try { pa = JSON.parse(raw); } catch { localStorage.removeItem("pending_action"); return; }
    if (pa.kind !== "org" || pa.id !== id) return;
    if (u.role !== "user") { localStorage.removeItem("pending_action"); toast.info(t("actionx.patient_only")); return; }
    if (pa.type === "book_service") {
      localStorage.removeItem("pending_action");
      nav(`/services/${pa.service_id}`);
      return;
    }
    if (pa.type === "shop_checkout") { localStorage.removeItem("pending_action"); setTab("shop"); setOpenCartShop(true); return; }
    localStorage.removeItem("pending_action");
    if (pa.type === "follow") { if (!isFollowing) api.post(`/professionals/${id}/follow`).then(load).catch(() => {}); }
    else if (pa.type === "chat") nav(`/chat/${id}`);
  }, [u, org, id, services]);
  if (err) return <Layout><div className="yp-container py-8 text-red-600" data-testid="org-error">{err}</div></Layout>;
  if (!org) return <Layout><div className="yp-container py-8">{t("common.loading")}</div></Layout>;
  const isOwn = u?.id === id && u?.role === "organization";
  return (<Layout><div className="yp-container max-w-3xl py-8">
    {org.cover_url && (
      <div className="w-full aspect-[3/1] rounded-lg overflow-hidden mb-4 bg-slate-100" data-testid="org-cover">
        <img src={org.cover_url} alt="" className="w-full h-full object-cover" />
      </div>)}
    <div className="yp-card p-6 yp-gradient-border" data-testid="org-public-profile">
      <div className="flex justify-between items-start gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Avatar url={org.avatar_url} label={org.org_name} org cls="w-16 h-16 text-xl" />
          <h1 className="text-3xl font-bold flex items-center gap-2">{org.org_name}{org.verified && <VerifiedBadge size={22} />}</h1>
        </div>
      </div>
      <div className="mt-4 text-slate-600 space-y-1 text-sm">
        {orgTypeLabel(org.org_type, t, org.org_type_other) && <div className="text-blue-700 font-medium" data-testid="org-type-label">{orgTypeLabel(org.org_type, t, org.org_type_other)}</div>}
        {org.city && <div>📍 {org.city}{org.country ? ` · ${org.country}` : ""}</div>}
        {org.phone && <div>📞 {org.phone}</div>}
      </div>
      {!isOwn && (
        <div className="mt-4 flex gap-2 flex-wrap">
          <button className="yp-btn yp-btn-primary" onClick={onFollow} data-testid="org-follow-btn">{isFollowing ? `✓ ${t("followx.following")}` : `+ ${t("followx.follow")}`}</button>
          <button className="yp-btn yp-btn-outline" onClick={onChat} data-testid="org-chat-btn">💬 {t("followx.chat")}</button>
          {hasEvents !== null && (hasEvents ? (
            <Link to={`/events?organization_id=${id}`} className="yp-btn yp-btn-primary focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="org-book-event-btn">📅 {t("orgd.book_event")}</Link>
          ) : (
            <button className="yp-btn yp-btn-outline opacity-60 cursor-not-allowed" disabled data-testid="org-no-events-btn">{t("orgd.no_events")}</button>
          ))}
        </div>)}
      {isOwn && (<div className="mt-4"><FollowersButton /></div>)}
      <div className="mt-4"><ShareButton url={org.public_url} title={`${org.org_name} — YouPAM`} /></div>
    </div>
    <PublicBio title={t("orgx.about_us")} text={org.public_bio} testid="org-about" />
    <div className="mt-8" data-testid="org-tabs-section">
      <div className="flex gap-2 mb-4 overflow-x-auto" role="tablist" data-testid="org-tabs">
        {[["posts", t("inperson.posts")], ...(org.tabs?.events ? [["events", t("nav.events")]] : []), ...(org.tabs?.services ? [["services", t("services.title")]] : []), ...(org.tabs?.shop ? [["shop", t("shop.title")]] : [])].map(([k, lbl]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
            className={`yp-btn !py-1.5 !px-4 text-sm shrink-0 ${tab === k ? "yp-btn-primary" : "yp-btn-outline"}`} data-testid={`org-tab-${k}`}>{lbl}</button>))}
      </div>
      {tab === "posts" && (
        <div data-testid="org-posts-section">
          {isOwn && <PostComposer onPosted={() => setPostsKey(k => k + 1)} />}
          <PostList key={postsKey} authorId={id} canManage={isOwn} />
        </div>)}
      {tab === "events" && (
        <div className="space-y-2" data-testid="org-events-section">
          {events.length === 0 ? <div className="text-slate-400" data-testid="org-events-empty">{t("events.empty")}</div>
            : events.map(e => (
              <div key={e.id} className="yp-card p-4 flex justify-between items-start gap-3 flex-wrap" data-testid="org-event-item">
                <Link to={`/events/${e.id}`} className="min-w-0 flex-1 hover:opacity-80">
                  <div className="font-medium flex items-center gap-2">{e.title}{e.is_past && <span className="yp-badge bg-slate-100 text-slate-600" data-testid="org-event-past-badge">{t("events.past_badge")}</span>}</div>
                  <div className="text-xs text-slate-500 mt-1">{e.is_paid ? `€ ${((e.price_cents||0)/100).toFixed(2)}` : t("events.free")}{e.slots?.[0] ? ` · ${new Date(e.slots[0].start_at).toLocaleString()}` : ""}</div>
                </Link>
                <ShareMenu shareUrl={`${window.location.origin}/p/evento-${e.id}`} title={e.title}
                  youpam={isOwn ? { entityType: "event", entityId: e.id, orgId: id } : undefined} className="!py-1 !px-3 !text-xs" />
              </div>))}
        </div>)}
      {tab === "services" && (
        <div className="space-y-2" data-testid="org-services-section">
          {services.length === 0 ? <div className="text-slate-400" data-testid="org-services-empty">{t("services.empty_public")}</div>
            : services.map(s => {
              const proLabel = s.professional ? (s.professional.free ? s.professional.name : formatProName({ title: s.professional.title, name: s.professional.name }, i18n.language)) : "";
              const loc = [...new Set([s.location_label, s.address].filter(Boolean))].join(" · ");
              return (
              <div key={s.id} className="yp-card p-4 flex justify-between items-start gap-3 flex-wrap" data-testid="org-service-item">
                <Link to={`/services/${s.id}`} className="min-w-0 flex-1 hover:opacity-80">
                  <div className="font-medium" data-testid="org-service-name">{s.name}</div>
                  {s.description && <div className="text-sm text-slate-600 mt-0.5 line-clamp-2">{s.description}</div>}
                  <div className="text-xs text-slate-500 mt-1">{s.price_cents ? `€ ${(s.price_cents/100).toFixed(2)}` : t("services.free")} · {s.duration_minutes} min{loc ? ` · ${loc}` : ""}</div>
                  {proLabel && <div className="text-xs text-slate-500 mt-0.5" data-testid="org-service-pro">{proLabel}</div>}
                </Link>
                <div className="flex flex-col gap-2 items-end shrink-0">
                  <Link to={`/services/${s.id}`} className="yp-btn yp-btn-primary !py-1 !px-3 text-xs" data-testid="org-service-book-btn">{t("services.book_service")}</Link>
                  <ShareMenu shareUrl={`${window.location.origin}/services/${s.id}`} title={s.name}
                    youpam={isOwn ? { entityType: "service", entityId: s.id, orgId: id } : undefined} className="!py-1 !px-3 !text-xs" />
                </div>
              </div>);})}
        </div>)}
      {tab === "shop" && <ShopTab orgId={id} orgName={org.org_name} openCart={openCartShop} openProductId={sp.get("product")} />}
    </div>
  </div></Layout>);
}
